// Traduction technique → langage business pour ALTIORA Leads
// Aucun mock, aucune valeur inventée. Si un code n'est pas mappé,
// on retourne la version originale — la donnée backend reste source de vérité.

export const SIGNAL_LABELS = {
  contract_won: "Contrat public gagné",
  public_tender_issued: "Appel d'offres public émis",
  new_incorporation: "Nouvelle immatriculation",
  status_change: "Changement de statut légal",
  press_mention: "Communiqué de presse",
  annual_filing_delinquent: "Déclaration annuelle en retard",
  user_import: "Contact importé de mon CRM",
  director_added: "Nouveau dirigeant nommé",
  director_removed: "Départ d'un dirigeant",
  address_change: "Changement d'adresse",
  recruitment_signal: "Recrutement en cours",
  event_appearance: "Présence à un événement",
};

export const SOURCE_LABELS = {
  PROCUREMENT_SEAO: "SEAO · marchés publics",
  REGISTRY_ISED: "Registre fédéral (ISED)",
  NEWS_CNW: "CNW · communiqués",
  USER_IMPORT: "Mon CRM",
};

export const KIND_LABELS = {
  Business: "Entreprise",
  Partner: "Organisme partenaire",
  DecisionMaker: "Décideur",
  Person: "Personne",
};

export const TIER_LABELS = {
  A: "Priorité #1",
  B: "À évaluer",
  C: "À surveiller",
  excluded: "Écarté",
};

// Le backend renvoie 6 statuts (solicitation_states). On les regroupe en
// 4 badges lisibles, avec règle stricte de non-inférence : tant que la
// donnée n'est pas prouvée, on affiche "info insuffisante".
export function contactabilityBadge(status) {
  const s = (status || "").toLowerCase();
  if (s === "allowed_lcap_10_9" || s === "user_managed") {
    return { key: "green", emoji: "🟢", label: "CONTACT POSSIBLE",
             color: "#1F8F5B", bg: "#D8F5E5",
             hint: "Cadre LCAP 10(9) ou contact que vous gérez." };
  }
  if (s === "off_limits") {
    return { key: "red", emoji: "🔴", label: "NE PAS SOLLICITER",
             color: "#B23C3C", bg: "#FCE1E1",
             hint: "Identité privée ou canal expressément interdit." };
  }
  if (s === "allowed_lcap_10_9_role_only"
      || s === "explicit_consent_required"
      || s === "channel_only_offline") {
    return { key: "amber", emoji: "🟠", label: "À VÉRIFIER",
             color: "#B76E00", bg: "#FDECC8",
             hint: "Autorisation partielle — vérifier le canal avant contact." };
  }
  return { key: "gray", emoji: "⚪", label: "INFO INSUFFISANTE",
           color: "#64748B", bg: "#E5E7EB",
           hint: "ALTIORA n'a pas trouvé de preuve suffisante pour statuer." };
}

export function tierBadge(tier) {
  if (tier === "A") return { label: TIER_LABELS.A, color: "#0B2545", bg: "#F5D687" };
  if (tier === "B") return { label: TIER_LABELS.B, color: "#0B2545", bg: "#F0E4C8" };
  if (tier === "C") return { label: TIER_LABELS.C, color: "#334155", bg: "#E5E7EB" };
  return { label: TIER_LABELS.excluded || tier, color: "#64748B", bg: "#E5E7EB" };
}

// Explication business quand plusieurs sources concordent
export function crossSourceLabel(sources) {
  const n = (sources || []).length;
  if (n >= 2) return `Plusieurs sources concordent (${n})`;
  if (n === 1) return `Une seule source (${(sources || [])[0] || ""})`;
  return "Sources non renseignées";
}

export function humanSignal(type) {
  return SIGNAL_LABELS[type] || type || "Signal";
}

export function humanSource(family) {
  return SOURCE_LABELS[family] || family || "Source";
}

export function humanKind(kind) {
  return KIND_LABELS[kind] || kind || "Cible";
}

// ─── Lifecycle ─────────────────────────────────────────────────
export const STATUS_LABELS = {
  NEW: "Nouveau",
  QUALIFIED: "Qualifié",
  TO_REVIEW: "À traiter",
  READY_TO_CONTACT: "Prêt à contacter",
  CONTACTED: "Contacté",
  FOLLOW_UP: "Suivi",
  WON: "Gagné",
  LOST: "Perdu",
  IGNORED: "Ignoré",
};

// Ordre canonique du cycle
export const STATUS_ORDER = [
  "NEW", "QUALIFIED", "TO_REVIEW", "READY_TO_CONTACT",
  "CONTACTED", "FOLLOW_UP", "WON", "LOST", "IGNORED",
];

export function humanStatus(status) {
  return STATUS_LABELS[status] || status || "?";
}

// Libellés courts pour les boutons d'action (transitions)
export const TRANSITION_BUTTON_LABELS = {
  QUALIFIED: "Qualifier",
  TO_REVIEW: "À traiter",
  READY_TO_CONTACT: "Prêt à contacter",
  CONTACTED: "Contacté",
  FOLLOW_UP: "Suivi",
  WON: "Gagné",
  LOST: "Perdu",
  IGNORED: "Ignorer",
  NEW: "Réactiver",
};

export function statusPillStyle(status) {
  const map = {
    NEW:              { bg: "#E5E7EB", color: "#334155" },
    QUALIFIED:        { bg: "#DBEAFE", color: "#1E3A8A" },
    TO_REVIEW:        { bg: "#FDECC8", color: "#B76E00" },
    READY_TO_CONTACT: { bg: "#F5D687", color: "#0B2545" },
    CONTACTED:        { bg: "#C7F0DC", color: "#166534" },
    FOLLOW_UP:        { bg: "#CFF6EA", color: "#0E7490" },
    WON:              { bg: "#1F8F5B", color: "#FFFFFF" },
    LOST:             { bg: "#B23C3C", color: "#FFFFFF" },
    IGNORED:          { bg: "#94A3B8", color: "#FFFFFF" },
  };
  return map[status] || map.NEW;
}

// ─── Close / ignore reasons ────────────────────────────────────
export const CLOSE_REASON_LABELS = {
  not_relevant: "Pas pertinent pour moi",
  bad_timing: "Mauvais timing",
  wrong_target: "Mauvaise cible",
  already_handled: "Déjà traité ailleurs",
  cannot_contact: "Impossible à contacter (compliance)",
  not_interested: "Prospect non intéressé",
  opportunity_converted: "Opportunité convertie",
  other: "Autre",
};

export function humanReason(reason) {
  return CLOSE_REASON_LABELS[reason] || reason || "";
}

// ─── Contactability V2 · per-channel badge ─────────────────────
// backend statuses (contactability_v2.py) : CONTACTABLE,
// VERIFICATION_REQUIRED, DO_NOT_CONTACT, UNKNOWN
export function channelBadge(status) {
  const s = (status || "").toUpperCase();
  if (s === "CONTACTABLE") {
    return { key: "green", dot: "#1F8F5B", bg: "#D8F5E5",
             color: "#0F5E39", label: "OUI" };
  }
  if (s === "VERIFICATION_REQUIRED") {
    return { key: "amber", dot: "#B76E00", bg: "#FDECC8",
             color: "#8A5200", label: "À VÉRIFIER" };
  }
  if (s === "DO_NOT_CONTACT") {
    return { key: "red", dot: "#B23C3C", bg: "#FCE1E1",
             color: "#8A2E2E", label: "NON" };
  }
  return { key: "gray", dot: "#94A3B8", bg: "#E5E7EB",
           color: "#64748B", label: "INCONNU" };
}

// Human-readable reason for a per-channel status (backend "reason" codes)
export const CHANNEL_REASON_LABELS = {
  aucune_coordonnee_trouvee:
    "Aucune coordonnée trouvée dans les sources.",
  consentement_natif_capture_par_formulaire:
    "Consentement natif capturé via formulaire ALTIORA.",
  lcap_10_9_coordonnee_publiee_role_pertinent:
    "LCAP 10(9) — coordonnée publiée pour un rôle pertinent.",
  courriel_professionnel_generique_verifier_pertinence_role_avant_envoi:
    "Courriel professionnel générique — vérifiez la pertinence du rôle avant envoi.",
  lnnte_verification_manuelle_requise:
    "Vérification LNNTE (Liste nationale de numéros de télécommunication exclus) manuelle requise.",
  identite_privee_ou_canal_interdit:
    "Identité privée ou canal expressément interdit.",
  base_legale_a_etablir:
    "Base légale à établir avant toute sollicitation.",
  high_confidence_match_pending_lnnte_verification:
    "Correspondance forte trouvée sur Canada411 — LNNTE reste à vérifier avant appel.",
  medium_confidence_manual_review:
    "Correspondance partielle — revue humaine avant appel.",
  low_confidence_not_associated:
    "Correspondance trop faible — non associée au lead.",
  suppression_list_hit:
    "Coordonnée présente dans la liste de suppression (DNC/DNE).",
};

export function humanChannelReason(reason) {
  if (!reason) return "";
  // strip suppression_list:<sub_reason> prefix
  const trimmed = reason.startsWith("suppression_list:")
    ? "suppression_list_hit" : reason;
  return CHANNEL_REASON_LABELS[trimmed] || reason;
}
